import React, { useState, useRef, useEffect } from "react";
import { HiOutlinePaperClip } from "react-icons/hi2";
import { IoMdCloseCircle } from "react-icons/io";
import { FaArrowAltCircleUp } from "react-icons/fa";
import { FaMicrophone } from "react-icons/fa";

const MessageInput = ({ handleSubmit, input, setInput, handleFileChange, showWork, messageCount = 0, disabled }) => {
  const [filePreview, setFilePreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const textareaRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (isCameraActive) {
      startCamera();
    }
  }, [isCameraActive]);

  useEffect(() => {
    initializeSpeechRecognition();
  }, []);

  const initializeSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        // Append to existing text instead of overwriting
        setInput(prev => {
          if (prev.trim()) {
            return prev + ' ' + transcript;
          }
          return transcript;
        });
        setIsRecording(false);
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  };

  const toggleRecording = () => {
    if (!speechSupported || !recognitionRef.current) return;

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Speech recognition error:', error);
        setIsRecording(false);
      }
    }
  };

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [input]);

  const handleFileChangeInternal = (file) => {
    if (file) {
      const fileType = file.type;
      if (fileType === "image/png" || fileType === "image/jpeg") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
        handleFileChange(file);
        setErrorMessage("");
      } else {
        setErrorMessage("Only PNG and JPG files are allowed.");
      }
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      const aspectRatio = 8.5 / 11; // 8.5" x 11" paper aspect ratio
      canvas.height = video.videoHeight;
      canvas.width = canvas.height * aspectRatio;

      const context = canvas.getContext("2d");

      const cropWidth = video.videoHeight * aspectRatio;
      const cropX = (video.videoWidth - cropWidth) / 2;

      context.drawImage(video, cropX, 0, cropWidth, video.videoHeight, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        const newCapturedFile = new File([blob], "captured_image.jpg", {
          type: "image/jpeg",
        });
        setFilePreview(URL.createObjectURL(newCapturedFile));
        handleFileChange(newCapturedFile); // Pass the file to the parent component
      });

      const stream = video.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      setIsCameraActive(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
      setFilePreview(null);
    }
  };

  // Always show both camera and paperclip buttons (removed showWork condition)
  const cameraButton = (
    <button
      type="button"
      onClick={() => setIsCameraActive(true)}
      className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-200"
      title="Take a photo"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-5 h-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
      </svg>
    </button>
  );

  const paperClip = (
    <button
      type="button"
      onClick={() => document.getElementById("file-upload").click()}
      className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-200"
      title="Upload an image"
    >
      <HiOutlinePaperClip size={20} />
    </button>
  );

  return (
    <div className="message-input-container">
      {isCameraActive && (
        <div className="video-container mb-4" style={{ maxWidth: '100%', margin: '0 auto' }}>
          <video
            ref={videoRef}
            className="border rounded object-cover"
            style={{ height: "360px", width: "100%", maxWidth: "270px" }}
            playsInline
          ></video>
          <button
            type="button"
            onClick={captureImage}
            className="w-full mt-2 mb-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Capture Image
          </button>
          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        </div>
      )}
      {filePreview && (
        <div className="p-2 bg-gray-200 rounded-t">
          <div className="inline-block relative">
            <button
              onClick={() => {
                setFilePreview(null);
                handleFileChange(null);
              }}
              className="text-sm text-red-500 absolute right-0 top-[-8px] hover:text-red-700 bg-white rounded-full cursor-pointer"
            >
              <IoMdCloseCircle size={20} />
            </button>
            <img
              src={filePreview}
              alt="Selected"
              className="w-16 h-16 object-cover rounded mr-2"
            />
          </div>
        </div>
      )}
      {errorMessage && <div className="text-red-500 mb-2">{errorMessage}</div>}

      {/* Camera Prompt Button - Only show when camera is NOT active */}
      {messageCount < 1 && !isCameraActive && (
        <button
          type="button"
          onClick={() => setIsCameraActive(true)}
          disabled={disabled}
          className={`w-full mb-3 py-4 px-4 text-white rounded-lg font-semibold flex items-center justify-center transition-all bg-green-500 hover:bg-green-600 shadow-md hover:shadow-lg ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
          </svg>
          <span className="text-base">
            Take a Picture of What You're Working On
          </span>
        </button>
      )}

      <form
        onSubmit={(e) => {
          handleSubmit(e);
          setFilePreview(null);
        }}
        className={`flex items-center gap-2 p-2 bg-gray-100 rounded ${
          filePreview ? "rounded-t-none" : "rounded"
        }`}
      >
        {cameraButton}
        {paperClip}
        <input
          id="file-upload"
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChangeInternal(e.target.files[0])}
          className="hidden"
        />
        <textarea
          ref={textareaRef}
          className="flex-grow p-2 m-0 border-0 focus:outline-none focus:ring-0 focus:border-transparent bg-transparent resize-none max-h-48 min-h-10"
          style={{ width: speechSupported ? '85%' : '90%' }}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            adjustHeight();
          }}
          placeholder={
            disabled
              ? "Select a subject to start chatting"
              : isRecording
              ? "Listening..."
              : "Type your message..."
          }
          rows={1}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />

        {/* Voice Input Button - Now inline with textarea */}
        {speechSupported && (
          <button
            type="button"
            onClick={toggleRecording}
            disabled={disabled}
            className={`flex items-center justify-center h-10 w-10 rounded-full transition-all ${
              isRecording
                ? 'bg-red-500 hover:bg-red-600 recording-button'
                : 'bg-purple-500 hover:bg-purple-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isRecording ? 'Stop Recording' : 'Speak Your Question'}
          >
            <FaMicrophone
              className={isRecording ? 'mic-recording text-white' : 'text-white'}
              size={18}
            />
          </button>
        )}

        <button
          type="submit"
          disabled={disabled || input.length === 0 && !filePreview}
          className={`outline-none disabled:opacity-75 flex items-center justify-center focus-visible:outline-none h-10 w-10 hover:opacity-70 rounded-full transition-colors ${
            disabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-700'
          }`}
        >
          <FaArrowAltCircleUp size={25} color={disabled ? "#5688F7" : "#FFFFFF"} />
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
